import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { put as putBE } from "utils/api";
import { message } from "actions/message";
import { invalidatePatientProfileInfo, fetchPatientProfileInfo } from "actions/patient";
import { STATES } from "utils/enum";
import FormikField from "components/forms/formik-field";
import { Formik } from "formik";
import StatesSelect from "components/forms/states-select/index";

const TaskAddressUpdateForm = ({ patientId, orderId, taskId, taskType, taskText, onSuccess }) => {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  
  // Determine if this is a permanent or temporary address change based on task type
  const isPermanent = taskType === "AC - Perm Address Change";
  
  // Create state name to abbreviation mapping from STATES
  const stateNameToAbbreviation = STATES.reduce((acc, state) => {
    acc[state.text] = state.value;
    return acc;
  }, {});
  
  // Parse address from task text
  const parseAddressFromTaskText = (text) => {
    if (!text) return {};
    
    const streetMatch = text.match(/Street:\s*([^,]+)/);
    const street2Match = text.match(/Street2:\s*([^,]+)/);
    const cityMatch = text.match(/City:\s*([^,]+)/);
    const stateMatch = text.match(/State:\s*([^,]+)/);
    const zipMatch = text.match(/Zip:\s*(\d+)/);
    
    // Convert state name to abbreviation if needed
    let stateValue = "";
    if (stateMatch) {
      const stateName = stateMatch[1].trim();
      // Check if it's already an abbreviation (2 letters) or needs conversion
      stateValue = stateName.length === 2 
        ? stateName.toUpperCase() 
        : (stateNameToAbbreviation[stateName] || stateName);
    }
    
    return {
      street: streetMatch ? streetMatch[1].trim() : "",
      street_line_2: street2Match ? street2Match[1].trim() : "",
      city: cityMatch ? cityMatch[1].trim() : "",
      state: stateValue,
      zip: zipMatch ? zipMatch[1].trim() : ""
    };
  };
  
  const parsedAddress = parseAddressFromTaskText(taskText);

  const updateAddress = async values => {
    dispatch(message("Updating Address..."));
    try {
      await putBE(`tasks/update-address`, values);
      dispatch(message("Address updated successfully", "success"));
      
      // Refresh patient profile if permanent address change
      if (isPermanent) {
        dispatch(invalidatePatientProfileInfo({ patientId }));
        dispatch(fetchPatientProfileInfo({ patientId }));
      }
      
      setShowForm(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      dispatch(message("Failed to update address", "error"));
    }
  };

  return (
    <div>
      {!showForm ? (
        <button onClick={() => setShowForm(true)}>
          {isPermanent ? "Change Permanent Address" : "Change Temporary Address"}
        </button>
      ) : (
        <Formik
          initialValues={{
            patient_guid: patientId,
            order_guid: orderId,
            task_id: taskId,
            street: parsedAddress.street || "",
            street_line_2: parsedAddress.street_line_2 || "",
            state: parsedAddress.state || "",
            zip: parsedAddress.zip || "",
            city: parsedAddress.city || "",
            permanent_address_change: isPermanent
          }}
        >
          {({ values, setFieldValue }) => (
            <form>
              <div
                style={{
                  display: "flex",
                  justifyItems: "center",
                  marginBottom: "10px"
                }}
              >
                <h3 style={{ margin: 0 }}>
                  {isPermanent ? "Update Permanent Address" : "Update Temporary Address"}
                </h3>
              </div>
              
              <FormikField name="street" label="Street Line 1" type="text" />
              <FormikField
                name="street_line_2"
                label="Street Line 2"
                type="text"
              />
              <FormikField name="city" label="City" type="text" />
              <StatesSelect
                label="State"
                value={values.state}
                onChange={(e, state) => setFieldValue("state", state.state)}
                name={"state"}
                disabled={false}
              />
              <FormikField name="zip" label="Zip Code" type="text" />

              <button
                type="button"
                style={{ marginRight: "10px" }}
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  values.city === "" ||
                  values.state === "" ||
                  values.street === "" ||
                  values.zip.length < 5
                }
                onClick={() => {
                  updateAddress(values);
                }}
              >
                Submit
              </button>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
};

TaskAddressUpdateForm.propTypes = {
  patientId: PropTypes.string.isRequired,
  orderId: PropTypes.string,
  taskId: PropTypes.string.isRequired,
  taskType: PropTypes.string.isRequired,
  taskText: PropTypes.string,
  onSuccess: PropTypes.func
};

export default TaskAddressUpdateForm;
